import { Layout, CheckCircle2, GripVertical, Plus, Trash2 } from "lucide-react";
import { confirmEmailWireframe } from "./action";
import jsCookie from "js-cookie";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* eslint-disable react/prop-types */
interface SectionData {
  id: string;
  title: string;
  height: number;
  fixed?: boolean;
}

interface NewsletterWireframeProps {
  response: {
    id: string;
    name: string;
    description: string;
    data: {
      title: string;
      height: number;
    }[];
  };
  onConfirm?: () => void;
}

const SECTION_TYPES = [
  { value: "banner", label: "Banner", height: 3 },
  { value: "main_article", label: "Main Article", height: 5 },
  { value: "secondary_articles", label: "Secondary Articles", height: 4 },
  { value: "call_to_action", label: "Call to Action", height: 2 },
];

interface SortableItemProps {
  section: SectionData;
  onDelete: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ section, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="w-full lg:w-[25vw] bg-gray-200 rounded mb-1 relative group"
      {...attributes}
    >
      <div
        className="h-full flex items-center justify-center text-gray-500 relative"
        style={{ height: `${section.height * 50}px` }}
      >
        <div
          {...listeners}
          className="absolute left-2 cursor-grab active:cursor-grabbing p-1 hover:bg-gray-300 rounded"
        >
          <GripVertical className="w-3 h-3 text-gray-600" />
        </div>
        <span className="text-sm">{section.title}</span>
        <button
          onClick={() => onDelete(section.id)}
          className="absolute right-2 top-2 p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-3 h-3 text-red-600" />
        </button>
      </div>
    </div>
  );
};
const NewsletterWireframe: React.FC<NewsletterWireframeProps> = ({ response, onConfirm }) => {
  const [searchParams] = useSearchParams();
  const userId = jsCookie.get("user_id");
  const threadId = `${searchParams.get("threadId")}`;
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const isHeader = (s: SectionData) => s.title === "Header";
  const isFooter = (s: SectionData) => s.title === "Footer";

  // Initialize sections with unique IDs
  const [sections, setSections] = useState<SectionData[]>(() =>
    response.data.map((item, index) => ({
      id: `section-${index}-${Date.now()}`,
      title: item.title,
      height: item.height,
      fixed: item.title === "Header" || item.title === "Footer",
    }))
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!userId || !threadId) {
    return <div>User ID is not available.</div>;
  }

  if (!response || !response.data || response.data.length === 0) {
    return <div>No wireframe data available.</div>;
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        // Create new array with updated positions
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Debug log to verify order change
        // console.log('Sections after drag:', newItems.map((s, i) => ({ order: i, title: s.title })));
        
        return newItems;
      });
    }
  };

  const handleAddSection = (sectionType: any) => {
    setSections((prev) => {
      const footerIndex = prev.findIndex(isFooter);
      const newSection = {
        id: `section-${Date.now()}`,
        title: sectionType.label,
        height: sectionType.height,
      };

      if (footerIndex === -1) return [...prev, newSection];

      return [
        ...prev.slice(0, footerIndex),
        newSection,
        ...prev.slice(footerIndex),
      ];
    });

    setShowAddMenu(false);
  };


  const handleDeleteSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id || s.fixed));
  };


  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      // FIXED: Send sections in their current order after any dragging
      // The 'sections' state array already reflects the dragged order
      const orderedData = sections.map((section, index) => ({
        id: section.id,          // Include ID for backend reference
        title: section.title,
        height: section.height,
        order: index,            // This index now correctly reflects dragged position
        fixed: section.fixed,    // Include fixed status if backend needs it
      }));
      
      // Debug log to verify the order being saved
  
      
      const result = await confirmEmailWireframe(userId, threadId, orderedData);
      if (result.success) {
        setIsConfirmed(true);
        onConfirm?.();
      }
    } catch (error) {
      console.error("Failed to confirm wireframe:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fixedSections = sections.filter((s) => s.fixed);
  const sortableSections = sections.filter((s) => !s.fixed);

  if (isConfirmed) {
    return (
      <div className="w-full py-6 px-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-3 justify-center">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-green-900">Wireframe Saved Successfully!</h3>
            <p className="text-sm text-green-700 mt-1">You can move ahead to the next step.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller py-3">
      <div className="px-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-blue-50 text-blue-600 border border-blue-100">
            Analysis View
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Layout className="w-3 h-3 text-gray-700" />
          <h2 className="text-sm font-semibold text-gray-900">Template Wireframe</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500 leading-relaxed mb-2 overflow-wrap break-word max-w-prose">
          Based on your content, here is the suggested structural layout. Each block represents a
          section's relative height.
        </p>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-col items-center mx-2">
          {/* Fixed Header */}
          {fixedSections
            .filter((s) => s.title === "Header")
            .map((section) => (
              <div
                key={section.id}
                className="w-full lg:w-[25vw] bg-gray-200 rounded mb-1 flex items-center justify-center text-gray-600 font-medium"
                style={{ height: `${section.height * 50}px` }}
              >
                {section.title}
              </div>
            ))}
          <SortableContext
            items={sortableSections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortableSections.map((section) => (
              <SortableItem
                key={section.id}
                section={section}
                onDelete={handleDeleteSection}
              />
            ))}
          </SortableContext>

          {fixedSections
            .filter((s) => s.title === "Footer")
            .map((section) => (
              <div
                key={section.id}
                className="w-full lg:w-[25vw] bg-gray-200 rounded mt-1 flex items-center justify-center text-gray-600 font-medium"
                style={{ height: `${section.height * 50}px` }}
              >
                {section.title}
              </div>
            ))}

          {/* Add Section Button */}
          <div className="relative w-full lg:w-[25vw] mt-2">
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Add Section</span>
            </button>

            {/* Dropdown Menu */}
            {showAddMenu && (
              <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                {SECTION_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleAddSection(type)}
                    className="w-full px-4 py-2 text-left hover:bg-blue-50 text-sm text-gray-700 hover:text-blue-600 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DndContext>
      
      <div className="flex justify-end space-x-1 mt-2 mr-2">
        <button
          type="button"
          onClick={handleConfirm}
          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-xs"
          disabled={isLoading}
        >
          {isLoading ? "Confirming..." : "Confirm"}
        </button>
      </div>
    </div>
  );
};

export default NewsletterWireframe;
